/**
 * 事实句式与文案（P1-1.7，"怎么说"的一半；"怎么画"在 ./registry.js）
 *
 * 规则：
 * 1. 只从载荷取字段填进句式，**不自己算结论**（D12）。这里允许出现"这个数怎么写"
 *    （千分位、小数位、"未采集"字样），不允许"这个数说明什么"。
 * 2. 占位符缺值时写"未采集"，**绝不留 `{activeDays}` 在页面上**，也**不在渲染路径上抛错**：
 *    真样例里就有合法缺失（S0 的 `dataCutoff` 为 null，服务端确实不知道数据截至哪天），
 *    而它在渲染路径上 —— 一抛就是整页降回 V1，看起来像"新版不可用"，其实是文案层拼不动。
 *    拼错键名这类真错由 `missingPlaceholders()` + buildChecks 在测试期拦（构建红，不拿真页面试错）。
 * 3. 数字一律带单位、带口径词（"天"/"次"/"小时"），避免"7"到底是 7 天还是 7 次的歧义。
 * 4. 全部页面文案集中在此，**禁止散进 JSX**（否则重演 C10/P7）。`buildChecks.test.js`
 *    会扫本目录里未被本文件登记的中文串，并扫 §5.6 禁用词表。
 */
import { DIMENSION_LABEL, PRIMARY_METRIC, BUCKET_NOTICES } from './registry';

/** 载荷里取不到的值一律写成这个，而不是空串或 0 —— 空与零都会被读成"知道结果是零" */
export const UNKNOWN_TEXT = '未采集';

/** score 的两种非数字形态（三态里的另外两态，§5.4 修正 ③） */
export const SCORE_ABSENT_TEXT = '暂不出分';
export const SCORE_PROVISIONAL_SUFFIX = '· 样本不足，临时分';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** 一位小数向上取整到整数：分数展示用（0.5 分不进位，避免"看着差半分实则进位"的错觉） */
export function formatScore(value) {
  const n = num(value);
  return n === null ? null : String(Math.round(n * 10) / 10);
}

/** 千分位 + 最多一位小数：热量/步数这类大数用，避免 "195565" 这种读不动的串 */
export function formatNumber(value, { maxFraction = 1 } = {}) {
  const n = num(value);
  if (n === null) return UNKNOWN_TEXT;
  const fixed = Math.round(n * 10 ** maxFraction) / 10 ** maxFraction;
  const [intPart, decPart] = String(fixed).split('.');
  const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart ? `${withSep}.${decPart}` : withSep;
}

/**
 * 填模板：`{name}` 占位符，缺值即抛错。
 *
 * @param {string} template 含 {placeholder} 的句式
 * @param {object} values 占位符 → 已格式化的字符串
 */
/**
 * 填模板：`{name}` 占位符，缺值写成"未采集"（见文件头规则 2：渲染路径上不抛错）。
 *
 * @param {string} template 含 {placeholder} 的句式
 * @param {object} values 占位符 → 已格式化的字符串
 */
export function fill(template, values = {}) {
  return String(template).replace(/\{(\w+)\}/g, (_, key) => {
    const v = values[key];
    if (v === null || v === undefined || v === '') return UNKNOWN_TEXT;
    return String(v);
  });
}

/**
 * 这条模板在给定上下文下缺哪几个键。
 * 只给测试用：运行时不抛，但"缺了什么"得能被人知道 —— 否则一个键名打错会安静地变成一片"未采集"。
 */
export function missingPlaceholders(template, values = {}) {
  const missing = [];
  String(template).replace(/\{(\w+)\}/g, (_, key) => {
    const v = values[key];
    if (v === null || v === undefined || v === '') missing.push(key);
    return '';
  });
  return missing;
}

/**
 * 页面级占位符（来自载荷顶层，与维级合并后进 fill）。
 *
 * 为什么把"本次未纳入哪几维"的文案也在这里算：`PAGE_COPY.indexExcluded` 带 `{excluded}` 占位符，
 * 而这个值又是从 `weights` 推出来的。若让调用方自己拼一个同名的键，一旦拼错就是
 * `fill()` 抛错→整页降级，而且只有到真出现部分参与时才会撞上。因本函数是纯键集合操作，
 * 不含任何大小编判（R16）。
 */
export function pageContext(profile) {
  const p = profile || {};
  const weights = p.weights || {};
  const declared = weights.dimensionsPct
    ? Object.keys(weights.dimensionsPct)
    : [];
  const applied = Array.isArray(weights.appliedDimensions)
    ? weights.appliedDimensions
    : [];
  const excluded = declared.filter((key) => applied.indexOf(key) === -1);

  return {
    nDays: num(p.nDays) === null ? null : String(p.nDays),
    windowDays: num(p.windowDays) === null ? null : String(p.windowDays),
    dataCutoff: p.dataCutoff || null,
    periodStart: p.periodStart || null,
    periodEnd: p.periodEnd || null,
    engineVersion: p.engineVersion || null,
    weightsVersion: (p.weights && p.weights.version) || null,
    appliedSumPct:
      num(p.weights && p.weights.appliedSumPct) === null
        ? null
        : String(p.weights.appliedSumPct),
    declaredSumPct:
      num(p.weights && p.weights.declaredSumPct) === null
        ? null
        : String(p.weights.declaredSumPct),
    schemaVersion: p.schemaVersion || null,
    // 没声明权重（S0/INSUFFICIENT）、或全部参与时给 null：那一行本来就不该出现，
    // 写成空串会让 fill 抛错，写成"无"又等于让运营读到一句废活
    excluded: excluded.length
      ? excluded.map((key) => DIMENSION_LABEL[key] || key).join('、')
      : null,
  };
}

/**
 * 把"这一维的载荷"整理成文案能直接用的上下文，避免每个模板自己深挖字段。
 *
 * ⚠️ 这里**不设** `nDays` / `dataCutoff`：它们是页面级字段，若在此写成 `null` 再与页面级
 * 上下文合并，`{...page, ...dim}` 会把页面级的真值覆盖成 null，`fill()` 随即抛错、
 * 整页降级 —— 一个键的归属写错，代价是全站看不到 V2。
 */
export function contextOf(dimension, dim) {
  const f = (dim && dim.features) || {};
  const s = (dim && dim.score) || {};
  const b = (dim && dim.baseline) || {};
  const target = (dim && dim.selfTarget) || {};
  const metric = PRIMARY_METRIC[dimension] || { label: '主指标', unit: '' };
  return {
    dimension: DIMENSION_LABEL[dimension] || dimension,
    activeDays: num(f.activeDays) === null ? null : String(f.activeDays),
    observableDays:
      num(f.observableDays) === null ? null : String(f.observableDays),
    mean:
      num(f.mean) === null ? null : `${formatNumber(f.mean)} ${metric.unit}`,
    p50: num(f.p50) === null ? null : `${formatNumber(f.p50)} ${metric.unit}`,
    metricLabel: metric.label,
    metricUnit: metric.unit || null,
    maxStreak: num(f.maxStreak) === null ? null : `${f.maxStreak} 天`,
    score: formatScore(s.value),
    provisional: s.provisional === true,
    baselineP25:
      num(b.p25) === null ? null : `${formatNumber(b.p25)} ${metric.unit}`,
    baselineP50:
      num(b.p50) === null ? null : `${formatNumber(b.p50)} ${metric.unit}`,
    baselineP75:
      num(b.p75) === null ? null : `${formatNumber(b.p75)} ${metric.unit}`,
    targetValue:
      num(target.value) === null
        ? null
        : `${target.value}${target.unit ? ` ${target.unit}` : ''}`,
  };
}

/** 桶码 → 正文。占位符只允许用 contextOf / pageContext 里有的键（buildChecks 卡这条）。 */
export const NOTICE_COPY = {
  dataStale:
    '这段时间上游没有新的真值，最后一条数据截至 {dataCutoff}。图上讲的是观察窗内的情况，不是"这几天的情况"。',
  // 不用 <b> 之类的标记：这段文字会被当纯文本渲染，标签会原样出现在页面上（想强调交给 tone）
  // 说人话：读者是运营，不是数据工程师。旧文案把"一个真值都没拿到"重复了一遍，
  // 还附了一句"这是没有记录、不是记录了 0"——那是内部口径备忘，而展示层已经把两者合并了。
  noData: '这段时间没有拿到这一维的数据：可能没有记录，也可能设备没同步。',
  lowEvidence:
    '本次观察窗只有 {nDays} 天能看到真值，低于最小可信样本线，先不给评价。',
  dimInsufficient: '这一维有 {activeDays} 天信号，量还不够。',
  // §5.6-5：目标来源不是用户自设时，页面上不得出现达成度比较。
  // 措辞刻意只说"这一维画什么"，不说"那句被禁的话是什么"——禁用词扫描不分语境，
  // 写在否定句里也一样会被截图读成肯定。
  noSelfTarget: '该用户没有自设目标：这一维只列次数与周分布。',
  targetIsDefault: '当前目标是系统默认值而非用户自设：这一维只列次数与周分布。',
};

/**
 * `facts[].key` → 展示名。真源是 enums.md 的 facts 表（含两个本期不产出的键，
 * 服务端提前给也要能画，不给就少一行，不报错）。
 */
export const FACT_LABEL = {
  activeTimes: '窗口内运动次数',
  activeMinutes: '窗口内运动总时长',
  steps: '窗口内步数合计',
  maxStreak: '最长连续运动',
  weeksObserved: '可观测周数',
  sleepHoursAvg: '真值日平均睡眠时长',
  sleepRegularity: '睡眠规律指数',
  dietRecords: '窗口内饮食记录次数',
  dietMacrosDays: '营养解析成功天数',
  mentalRecords: '窗口内心理记录次数',
  mentalMinutes: '冥想与读书时长',
  rhr: '最近一次静息心率',
  hrv: '最近一次心率变异性',
};

/**
 * 分数那一行怎么写：三态各有说法，缺分绝不能写成 0 分。
 *
 * 入参是**服务端的 score 对象**（`{value,observed,provisional}`）而不是已格式化的上下文：
 * 上一版在这里吃 `ctx.score`（已经格式好的串），调用方一多就会出现"有人传串、有人传数"，
 * 而 `observed=false` 与 `value=null` 谁优先就没人管了。三态只能在一个地方读。
 */
export function scoreLine(score) {
  const s = score || {};
  const text = formatScore(s.value);
  if (s.observed === false || text === null) {
    return { text: SCORE_ABSENT_TEXT, tone: 'muted' };
  }
  if (s.provisional === true) {
    return {
      text: `${text} ${SCORE_PROVISIONAL_SUFFIX}`,
      tone: 'provisional',
    };
  }
  return { text, tone: 'normal' };
}

/** 基线带那一行 */
export function baselineLine(ctx) {
  if (!ctx.baselineP25 || !ctx.baselineP75) {
    return { text: '暂无个人基线', tone: 'muted' };
  }
  return {
    text: `个人基线 P25–P75：${ctx.baselineP25} ~ ${ctx.baselineP75}（中位 ${ctx.baselineP50}）`,
    tone: 'normal',
  };
}

/** 目标那一行：source 决定措辞，前端不看数值判"达标" */
export function targetLine(ctx) {
  if (!ctx.targetValue) return { text: '无自设目标', tone: 'muted' };
  return { text: `目标 ${ctx.targetValue}/周`, tone: 'normal' };
}

/** 一条事实 → 一句人话。observed=false 时不显示数值，只显示"未采集"。 */
export function factLine(fact) {
  if (!fact) return null;
  const value =
    fact.observed === false || fact.value === null || fact.value === undefined
      ? UNKNOWN_TEXT
      : `${formatNumber(fact.value)} ${fact.unit || ''}`.trim();
  return {
    key: fact.key,
    label: FACT_LABEL[fact.key] || null,
    value,
    observed: fact.observed !== false,
  };
}

/**
 * 指标行的窗口汇总（逐日矩阵里指标名下那一行）。
 *
 * 口径跟着指标走（registry.DIMENSION_METRICS 的 `aggregate` 传进来）：合计 / 均值 / 最近一次。
 * "睡眠时长求和"在页面上是错的，所以这里不能一套口径走到底；天数一并写出来，
 * 否则"合计 120 千卡"会被读成"窗口内就吃了 120 千卡"（实际只在那 N 天有真值）。
 */
export function aggregateLine(agg, unit) {
  if (!agg || agg.value === null || agg.value === undefined) return null;
  const value = formatNumber(agg.value);
  if (agg.kind === 'mean') return LABEL.meanLine(value, unit, agg.count);
  if (agg.kind === 'latest') return LABEL.latestLine(value, unit, agg.date);
  return LABEL.sumLine(value, unit, agg.count);
}

/**
 * 峰值那一行。柱状图没有 y 轴，不给"峰值 44 分钟"这种量纲线索，读者无法判断柱高含义。
 * 没有峰值（这一行没有任何记录）就返回 null —— 调用方不画这一行。
 */
export function peakLine(peak, unit, scopeLabel) {
  if (peak === null || peak === undefined) return null;
  return LABEL.peakLine(formatNumber(peak), unit, scopeLabel);
}

/**
 * 页头副标题：三段各自可选，填不满的那段直接不写（而不是写一段"数据截至 未采集"把整行拉长）。
 *
 * 真样例里就有这段拿不到的：`cold-start`（S0，整窗零可观测天）的 `dataCutoff` 是 null ——
 * 服务端确实不知道"数据截至哪天"。当时 `fill()` 选择抛错，这个合法载荷就把整页砸降回 V1，
 * 看上去像"新版不可用"。现在缺失只在**这一段**上发生，不再影响其他内容。
 *
 * 分隔符在这里而不在文案里：文案里写死 ` · ` 会让"某段缺失"在页面上留下两个点。
 */
export function metaLine(ctx) {
  return [PAGE_COPY.metaWindow, PAGE_COPY.metaCutoff, PAGE_COPY.metaCollected]
    .filter((template) => !missingPlaceholders(template, ctx).length)
    .map((template) => fill(template, ctx))
    .join(' · ');
}

/**
 * 桶码提示条的正文。只认已登记的码：未登记码返 null，由调用方走埋点 ——
 * 宁可少一条提示，也不让页面出现一条只有标题没有内容的空块。
 */
export function noticeText(code, values) {
  const meta = BUCKET_NOTICES[code];
  if (!meta) return null;
  const template = NOTICE_COPY[meta.copyKey];
  return template ? fill(template, values) : null;
}

/** 页面级固定文案。都放这里，是为了让"改一句话"只需要改一个文件（R15 的缓解）。
 * 占位符同 NOTICE_COPY 规则。
 */
export const PAGE_COPY = {
  // 2026-09-17 拍板：页面标题整个去掉。原先那行是「习惯行为指数（42 天观察窗）」，
  // 三条理由：① 页面只给四维各自的分，**没有总分**；② 上方还挂着从 V1 移过来的
  // 仪表盘/时段偏好/6 周趋势，进一步稀释了“指数”这个词；③ 紧接着下面就有一行
  // 观察窗说明，标题里再写一次“42 天观察窗”是重复。
  methodTrigger: '评估方法',
  stageLabel: '数据覆盖',
  noIndex: '暂不给分',
  // 降级时只留一行：外壳马上会把整页换回 V1，这里画多了就是两页堆叠
  degradeHint: '新版数据不可用，准备回退旧版展示。',
  // 副标题三段各自可选（见 metaLine）：S0 的 dataCutoff 合法为 null，不能因为它抛错
  metaWindow: '观察窗 {periodStart} ~ {periodEnd}（{windowDays} 天）',
  metaCutoff: '数据截至 {dataCutoff}',
  // 2026-09-17：原文是「共采集 {nDays} 天」，运营读成“只采集到 4 天”（以为整窗只有 4 天），
  // 而它真正要表达的是“42 天里有 4 天拿到了数据”。歧义来自**没有分母**，所以把 {windowDays} 写进来。
  // {nDays} 是**有真值的天数（全维并集）**，不是“有日报行”的天数（R18）。
  metaCollected: '{windowDays} 天中 {nDays} 天有数据',
  // §5.3 硬要求：窗口 42 天只支持"21 天不足以判定习惯"，同时提醒 42 天仍短 ⇒ 这句必须上页面
  // 窗口口径已改为“42 天截止昨天、按 7 天一组”（2026-09-16 拍板）：
  // 原来那句“6 个完整自然周”已经不成立，留着就是错话。
  // 但“不是习惯养成的判定”这句必须留：它是 §5.3 的硬要求（42 天也不足以判定习惯），
  // 精简文案不能把这种免责说明当废话删掉。
  windowNote:
    '观察窗为最近 42 天（截止昨天），按 7 天一组呈现；这是行为统计窗口，不是习惯养成的判定。',
  // 窗口天数不再在卡片里重复（页头标题已经写了“42 天观察窗”）
  progress: '数据积累中：{nDays} 天可观测',
  indexWeight:
    '本次参与合成的维度权重合计 {appliedSumPct}%（声明合计 {declaredSumPct}%）',
  indexExcluded: '本次未纳入：{excluded}（证据不足）',
  // D10：单维模式页脚固定一行免责。
  // 「与商家端不同口径、数字不可互推」那半句不在这里：它是内部两套系统的对账口径，
  // 运营看到只会觉得“此地无银”，而且它已经在「评估方法」抽屉里说全了（method.js，R5）。
  footerSingle: '这是单维行为指数，不是综合健康评价。',
  footerDefault:
    '以上数字为该会员自报与设备数据的统计结果，不构成健康评价或医学建议。',
  collectHint:
    '数据来源：App 内运动/饮食/心情打卡 + 已绑定设备的步数与睡眠同步。',
  definitionTitle: '这一维在讲什么',
};

/**
 * 日历格三态的人话（series.js 只返状态码，文字只能集中在本文件）。
 * `unreported` 与 `uncollected` 必须词面上就能分开：一个是链路问题，一个是用户没做。
 */
export const CELL_STATE_TEXT = {
  observed: '有真值',
  uncollected: UNKNOWN_TEXT,
  unreported: '上游未给出这一天',
  absent: '窗口外的日期',
};

/**
 * 短标签。与 PAGE_COPY 分两堆是因为：这里改一个词不会弄坏句式占位符校验，
 * 而 PAGE_COPY 里的每一句都得能被载荷填充满。
 */
/**
 * 一周七天的字。窗口按完整自然周对齐（D15），所以第一列就是周一 —— 位置即星期，
 * 这里不做任何日期换算（也不引 moment）。
 */
const WEEKDAY_CHARS = ['一', '二', '三', '四', '五', '六', '日'];

/** 参数化的短标签里，null/undefined/空串统一写"未采集"；0 是合法值，不能被吃掉 */
const safeDay = (value) =>
  value === null || value === undefined || value === '' ? UNKNOWN_TEXT : value;

export const LABEL = {
  meanSuffix: '均值',
  p50: '中位',
  maxStreak: '最长连续',
  perWeek: '每周',
  weeklyTitle: (weeks, unit) =>
    `近 ${weeks} 周（单位：${unit}/周；斜纹=窗口未覆盖那一周）`,
  dailyTitle: (label, unit) => `${label}逐日（${unit}）`,
  timesUnit: '次',
  weekOrdinal: (index) => `第${index + 1}周`,
  weekValue: (index, value, unit) => `第${index + 1}周：${value}${unit || ''}`,
  weekVoid: (index) => `第${index + 1}周：窗口未完整覆盖这一周（不是没做）`,
  baselineEmpty: `${UNKNOWN_TEXT}：暂无个人基线`,
  baselineP: (tag, value, unit) => `${tag} ${value}${unit || ''}`,
  alignHint: '（窗口未按自然周对齐）',
  calendarEmpty: '本窗口没有可观测数据',

  /* ---- 数据指标趋势（MetricTrends）---- */
  trendTitle: '数据指标趋势',
  trendHint:
    '每行一个指标，横着比是周与周（或天与天）的变化；没有柱的那段就是没有记录。列与列对齐，同一天各维分别记了什么，竖着看即可。',
  trendWindowNote: '观察窗固定 42 天',
  granularityLabel: '粒度',
  granularityDay: '日',
  granularityWeek: '周',
  /** 窗内没有记录、但本周那几天有记录时的汇总位文字（别让人以为那些柱算进了合计） */
  judgedEmpty: '观察窗内没有记录',
  /** 本周（未满）那一段的说明：位置 + 不影响什么，两句都说清 */
  recentNote:
    '最右侧带虚线的是本周（未满）：只作参考，不计入 42 天观察窗，也不计入左侧汇总。',
  partialMark: '（未满）',
  noRecord: '没有记录',
  peakScopeDay: '单日',
  peakScopeWeek: '单周',
  peakLine: (value, unit, scope) =>
    `峰值 ${value}${unit ? ` ${unit}` : ''}（${scope}）`,
  dayBarTip: (date, text) => `${date}：${text}`,
  weekBarTip: (range, text) => `${range}：${text}`,
  weekRange: (start, end) => `${start} ~ ${end}`,
  weekHeadLabel: (start, end) => `${start.slice(5)}~${end.slice(5)}`,
  weekdayShort: (index) => WEEKDAY_CHARS[index % WEEKDAY_CHARS.length],
  /** 日期键 → "几号"（窗口不再按自然周对齐，星期几推不出来，也不再需要） */
  dayOfMonth: (key) => String(key).slice(8),
  signalDays: (active, observable) =>
    `信号 ${safeDay(active)}/${safeDay(observable)} 天`,
  /* ---- 卡片三层信息用到的短标签（2026-09-16 重设计）---- */
  /** 第三格大号数字的口径：这一维在窗口内“有记录”的天数 */
  activeDaysLabel: '有记录',
  /** 天数单位（大号数字旁边不加括号时用） */
  dayUnit: '天',
  /** 主数字的退化口径（服务端没有对应事实行时）：主指标的窗口合计 */
  heroTotal: '窗口合计',

  /* ---- 目标进度（卡片内，2026-09-17 新增）----
   * 原本这是一句结论（“你自设的目标是每周 4 次…”），运营说那是数字、不该占一行；
   * 现在它是卡片里的一行 + 一根进度条。“你”也一并改成“该用户”（主语是运营在看别人）。
   */
  targetTitle: '每周目标',
  targetActual: (actual, unit) => `实际 ${actual} ${unit}`,
  targetGap: (delta, unit) => `差 ${delta} ${unit}`,
  targetReached: '已达成',

  /* ---- 三大营养素供能占比（饮食卡片，2026-09-17 新增）---- */
  macroTitle: '三大营养素供能占比',
  macroNote: '供能占比按每克 蛋白质/碳水 4 千卡、脂肪 9 千卡折算',
  macroGram: '克',
  noMetricData: (names) =>
    `窗口内没有记录的指标：${[].concat(names).join('、')}`,
  sumLine: (value, unit, days) =>
    `合计 ${value}${unit ? ` ${unit}` : ''} · ${days} 天有记录`,
  meanLine: (value, unit, days) =>
    `均值 ${value}${unit ? ` ${unit}` : ''} · ${days} 天有记录`,
  latestLine: (value, unit, date) =>
    `最近一次 ${value}${unit ? ` ${unit}` : ''}${date ? `（${date}）` : ''}`,
};

/**
 * 「指数概览」——V1 第一行（2026-09-16 拍板：永久保留）。图上的字全在这里，组件里一个字不写。
 */
export const OVERVIEW_COPY = {
  dialTitle: '运动习惯指数',
  prefTitle: (days) => `近 ${days} 天运动时段偏好`,
  // 不用 V1 原句里的"达标"（它落在禁用词表里）；意思一样：实际 vs 目标
  weekTitle: '近 6 周趋势（实际 vs 目标）',
  // V1 的四段与配色照搬：饼图的时段划分与颜色是它的产品语义，不是随手选的
  prefSlots: [
    { key: 'morning', label: '上午', color: '#2fc3ff' },
    { key: 'afternoon', label: '下午', color: '#13c2c2' },
    { key: 'evening', label: '晚上', color: '#3a5ad9' },
    { key: 'night', label: '夜间', color: '#8a5cd9' },
  ],
  dialCoefficient: (value) => `系数：${value}`,
  // 折线两条序列的名字（echarts 图例用）
  actualName: '实际',
  targetName: '目标',
  chartUnavailable: '图表库未加载',
  noData: '本期没有数据',
};

/**
 * 每维结论的句式（2026-09-16 拍板：档位 B）。
 *
 * B 档 = 允许引外部参考值（WHO/AASM）、允许描述性评价；底线只剩“不做医学诊断、不编造查不到出处的阈值”。
 * 主语一律是“该用户”而不是“你”：这一页是运营/管理员看的会员档案，不是会员自助页 ——
 * 写成“你”会让运营在读的时候错位成“我”，(2026-09-17 拍板改)。
 */
export const CONCLUSION_COPY = {
  baselineIn: (mean, unit, p25, p75) =>
    `窗口均值 ${mean} ${unit}，落在该用户自己 P25–P75（${p25} ~ ${p75}）之内。`,
  baselineOver: (mean, unit, p25, p75) =>
    `窗口均值 ${mean} ${unit}，高于该用户自己 P25–P75（${p25} ~ ${p75}）。`,
  baselineUnder: (mean, unit, p25, p75) =>
    `窗口均值 ${mean} ${unit}，低于该用户自己 P25–P75（${p25} ~ ${p75}）。`,

  /* ---- B 档（ 2026-09-16 拍板）：允许引外部参考值、允许描述性评价 ---- */
  sportGuide: (weekly) =>
    `窗口内平均每周 ${weekly} 分钟；世卫组织的参考值是每周至少 150 分钟中等强度活动。`,
  sleepGuide: (mean) =>
    `窗口内平均每天睡 ${mean} 小时；美国睡眠医学会的参考值是成年人每晚 7–9 小时。`,
  trendUp: (slope) => `近 6 周整体在上升（每周约 +${slope}）。`,
  trendDown: (slope) => `近 6 周整体在下降（每周约 ${slope}）。`,
  trendFlat: '近 6 周整体持平。',

  /* ---- 运动时段偏好（2026-09-16 新增）----
   * 卡片原来摆“早/午/晚/夜”四个次数，运营看不出意义；改成把这段分布读成一句偏好。
   * 分布本身已经在第一行的时段偏好图里，这里只出结论（而不是再把四个数拄一遍）。
   */
  prefDominant: (slot, share) =>
    `该用户的运动时间集中在${slot}（占 ${share}）。`,
  prefLeaning: (slot, share) =>
    `该用户的运动时间略偏向${slot}（占 ${share}）。`,
  prefEven: (slot, share) =>
    `该用户的运动时段分布较均匀（${slot}最多，占 ${share}）。`,

  /* ---- 窗口内一条记录都没有（饮食/心理，2026-09-16 新增）----
   * 原来这里说的是“窗口内 0 天有记录，最长连续 0 天”——两个统计数字，读不出人话；
   * 换成一句直白的结论。
   */
  notFormed: (what) => `该用户尚未养成记录${what}的习惯。`,
};

/**
 * 外部参考值（B 档）。**只有查得到出处的才写**（出处登记在 method.js 的 CITATIONS）；
 * 饮食与心理没有公认的单一参考值 —— 宁可只给事实，也不自编一个阈值。
 */
export const GUIDELINE = {
  SPORT: { weeklyMinutes: 150 },
  SLEEP: { minHours: 7, maxHours: 9 },
};

/** S0 指标定义卡（§5.5：S0 只给定义卡 + 空日历轨 + 采集途径，不给任何数字） */
export const DEFINITION_COPY = {
  SPORT: '运动：窗口内有训练记录的日子，看频次、总时长与连续性。',
  SLEEP: '睡眠：设备同步的睡眠时长（小时），没有设备同步就没有这一维。',
  DIET: '饮食：App 内饮食记录次数与解析出的热量，记录本身就是这一维的全部信号。',
  MENTAL:
    '心理：心情/冥想/读书的打卡次数与时长，只统计发生与连续，不做情绪评价。',
};

export { num };
